// src/api.js
export const API_BASE_URL = "/api1";

// ---------- SESSÃO: refresh automático em 401 ----------
// Uma única promise de refresh compartilhada entre chamadas concorrentes: se três requisições
// levam 401 ao mesmo tempo, disparam um só POST /auth/token/refresh/, não três.

let refreshPromise = null;
let sessionExpiredHandler = null;

export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

let licencaSuspensaHandler = null;

export function setLicencaSuspensaHandler(fn) {
  licencaSuspensaHandler = fn;
}

async function avisarSeLicencaSuspensa(res) {
  if (res.status !== 403 || !licencaSuspensaHandler) return;
  try {
    const corpo = await res.clone().json();
    if (corpo?.code === "licenca_suspensa") licencaSuspensaHandler();
  } catch {
    // corpo que não é JSON não é o 403 da licença
  }
}

function isRefreshUrl(url) {
  return url.includes("/auth/token/refresh/");
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Wrapper de fetch com sessão: em 401 (exceto na própria chamada de refresh), renova o
 * access token uma vez e repete a requisição original. Se a renovação falhar, aciona o
 * handler de sessão expirada (logout) e devolve a resposta 401 original.
 */
export async function authFetch(url, options = {}) {
  const opts = { credentials: "include", ...options };
  let res = await fetch(url, opts);

  if (res.status === 401 && !isRefreshUrl(url)) {
    const ok = await refreshSession();
    if (ok) {
      res = await fetch(url, opts);
    } else if (sessionExpiredHandler) {
      sessionExpiredHandler();
    }
  }

  await avisarSeLicencaSuspensa(res);
  return res;
}

function buildQuery(paramsObj) {
  const params = new URLSearchParams();

  Object.entries(paramsObj).forEach(([key, value]) => {
    if (value == null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== "" && v != null) params.append(key, v);
      });
    } else {
      params.append(key, value);
    }
  });

  return params.toString();
}

export async function previewMailing(filters) {
  const qs = buildQuery(filters);
  const url = `${API_BASE_URL}/preview/?${qs}`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Erro na API (${res.status}): ${text}`);
  }

  return res.json();
}

// ---------- EXPORTAÇÃO ASSÍNCRONA ----------

export async function iniciarExportacaoAssincrona(payload) {
  const url = `${API_BASE_URL}/async/tasks/`;

  const res = await authFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    let msg = `Erro ao iniciar exportação (${res.status})`;

    try {
      const data = JSON.parse(text);
      if (data.erro) msg = data.erro;
      else if (data.detail) msg = data.detail;
    } catch {
      if (text) msg = text;
    }

    throw new Error(msg);
  }

  return res.json();
}

export function parseRetryAfter(valor) {
  const segundos = Number(valor);
  if (!valor || !Number.isFinite(segundos) || segundos < 0) return null;
  return Math.min(segundos, 120) * 1000;
}

export function erroDePollingTransitorio(err) {
  const status = err && err.status;
  if (!status) return true;
  return status === 408 || status === 429 || status >= 500;
}

export async function verificarProgressoTask(taskId) {
  const url = `${API_BASE_URL}/async/tasks/${taskId}/`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const err = new Error(`Erro ao verificar progresso (${res.status})`);
    err.status = res.status;
    err.retryAfterMs = parseRetryAfter(res.headers.get("Retry-After"));
    err.detalhe = data && (data.erro || data.detail);
    throw err;
  }

  return data || {};
}

export async function baixarArquivoTask(taskId) {
  const url = `${API_BASE_URL}/async/tasks/${taskId}/download/`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text();
    let msg = `Erro ao baixar arquivo (${res.status})`;
    try {
      const data = JSON.parse(text);
      if (data.erro) msg = data.erro;
      else if (data.detail) msg = data.detail;
    } catch {
      if (text) msg = text;
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();

  const disposition = res.headers.get("Content-Disposition") || "";
  let finalName = "lista_empresas.zip";

  const match = disposition.match(/filename="?([^"]+)"?/i);
  if (match && match[1]) finalName = match[1];

  const urlBlob = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = urlBlob;
  a.download = finalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(urlBlob);
}


// ---- Listas auxiliares (cidades, CNAEs) ----

export async function listarCidadesTodas() {
  const url = `${API_BASE_URL}/cidades-todas/`;
  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Erro ao listar cidades (${res.status}): ${text}`);
  }

  return res.json();
}

export async function listarCnaes() {
  const url = `${API_BASE_URL}/cnaes/`;
  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Erro ao listar CNAEs (${res.status}): ${text}`);
  }

  return res.json();
}

export async function listarBairros(uf, municipios) {
  let municipiosParam = municipios;

  if (typeof municipios === "string") {
    municipiosParam = municipios
      .split(",")
      .map((m) => m.trim())
      .filter(Boolean);
  }

  const qs = buildQuery({ uf, municipios: municipiosParam });
  const url = `${API_BASE_URL}/listar_bairros/?${qs}`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Erro ao listar bairros (${res.status}): ${text}`);
  }

  const data = await res.json();
  return Array.isArray(data) ? data : [];
}


// ---------- ADMIN PANEL ----------

export async function adminListUsers(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/admin/usuarios/?${qs}`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }

  return res.json();
}

export async function adminToggleUserStatus(userId, isActive) {
  const url = `${API_BASE_URL}/admin/usuarios/${userId}/status/`;

  const res = await authFetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ is_active: isActive }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }

  return res.json();
}

export async function adminResetPassword(userId) {
  const url = `${API_BASE_URL}/admin/usuarios/${userId}/reset-senha/`;

  const res = await authFetch(url, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }

  return res.json();
}

export async function adminGetAudit(userId) {
  const url = `${API_BASE_URL}/admin/auditoria/?user_id=${userId}`;

  const res = await authFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }

  return res.json();
}

export async function adminUpdatePlan(userId, payload) {
  const url = `${API_BASE_URL}/admin/usuarios/${userId}/plano/`;

  const res = await authFetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }

  return res.json();
}

export async function adminCreateUser(payload) {
  const url = `${API_BASE_URL}/admin/usuarios/`;

  const res = await authFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminUpdateRole(userId, role) {
  const url = `${API_BASE_URL}/admin/usuarios/${userId}/role/`;

  const res = await authFetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ role }),
  });

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminResendInvite(userId) {
  const url = `${API_BASE_URL}/admin/usuarios/${userId}/invite/`;

  const res = await authFetch(url, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
